import { searchFilters } from '@common-types';

import { FILTERS_DATA as FiltersData } from '@interfaces/allScholarships';

export const MAIN_FILTERS_VALUES = [
  'keywords',
  'stateResidence',
  'nonEssay',
  'essay',
  'militaryService',
  'noApplicationFee',
  'scholarship',
  'fellowship',
  'grant',
  'forgivableLoan',
  'prize',
  'moneyRange',
  'freshman',
  'sophomore',
  'junior',
  'senior',
  'firstProfessionalDegree',
  'postgraduate',
  'university',
  'twoYearCollege',
  'fourYearCollege',
  'technicalSchool',
];

const initialAreas = searchFilters.areasOfStudy?.filters?.reduce(
  (accumulator, { label, value }) => ({
    ...accumulator,
    [`area${value}`]: false,
  }),
  {},
);

export const INITIAL_MAIN_FILTERS: { [key: string]: unknown } = {
  keywords: '',

  nonEssay: false,
  essay: false,
  militaryService: false,
  noApplicationFee: false,

  scholarship: false,
  fellowship: false,
  grant: false,
  forgivableLoan: false,
  prize: false,

  moneyRange: [],

  freshman: false,
  sophomore: false,
  junior: false,
  senior: false,
  firstProfessionalDegree: false,
  postgraduate: false,

  university: false,
  twoYearCollege: false,
  fourYearCollege: false,
  technicalSchool: false,

  stateResidence: [],

  institutionAid: false,
  privateAid: false,
  governmentAid: false,

  generalAreas: false,
  ...initialAreas,

  americanIndianAlaskaNative: false,
  asianPacificIslander: false,
  blackNonHispanic: false,
  hispanic: false,

  male: false,
  female: false,
  married: false,
  single: false,
  adultLearning: false,
  partTimeStudents: false,

  hearing: false,
  visual: false,
  physical: false,
  learning: false,
};

const areaFiltersData = searchFilters.areasOfStudy.filters.reduce(
  (accumulator, { label, value }) => ({
    ...accumulator,
    [`area${value}`]: {
      label,
      value: `area${value}`,
    },
  }),
  {},
);

export const FILTERS_DATA: FiltersData = {
  keywords: {
    title: 'awards worth up to',
    fieldValue: 'keywords',
  },

  requirements: {
    title: 'requirements',
    fields: {
      nonEssay: {
        value: 'nonEssay',
        label: 'Non essay',
      },
      essay: {
        value: 'essay',
        label: 'Essay',
      },
      militaryService: {
        value: 'militaryService',
        label: 'Military Service',
      },
      noApplicationFee: {
        value: 'noApplicationFee',
        label: 'No Application fee',
      },
    },
  },

  type: {
    title: 'type',
    fields: {
      scholarship: {
        value: 'scholarship',
        label: 'Scholarship',
      },
      fellowship: {
        value: 'fellowship',
        label: 'Fellowship',
      },
      grant: {
        value: 'grant',
        label: 'Grant',
      },
      forgivableLoan: {
        value: 'forgivableLoan',
        label: 'Forgivable loan',
      },
      prize: {
        value: 'prize',
        label: 'Prize',
      },
    },
  },

  moneyRange: {
    title: 'awards worth up to',
    fieldValue: 'moneyRange',
  },

  levelOfStudy: {
    title: 'level of study',
    fields: {
      freshman: {
        value: 'freshman',
        label: 'Freshman',
      },
      sophomore: {
        value: 'sophomore',
        label: 'Sophomore',
      },
      junior: {
        value: 'junior',
        label: 'Junior',
      },
      senior: {
        value: 'senior',
        label: 'Senior',
      },
      firstProfessionalDegree: {
        value: 'firstProfessionalDegree',
        label: 'First professional degree',
      },
      postgraduate: {
        value: 'postgraduate',
        label: 'Postgraduate',
      },
    },
  },

  institutionType: {
    title: 'institution type',
    fields: {
      university: {
        value: 'university',
        label: 'University',
      },
      twoYearCollege: {
        value: 'twoYearCollege',
        label: 'Two-year college',
      },
      fourYearCollege: {
        value: 'fourYearCollege',
        label: 'Four-year college',
      },
      technicalSchool: {
        value: 'technicalSchool',
        label: 'Trade / Technical school',
      },
    },
  },

  stateResidence: {
    title: 'state residence',
    fieldValue: 'stateResidence',
  },

  awardClassification: {
    title: 'award classification',
    fields: {
      institutionAid: {
        value: 'institutionAid',
        label: 'Institutional Aid',
      },
      privateAid: {
        value: 'privateAid',
        label: 'Private Aid',
      },
      governmentAid: {
        value: 'governmentAid',
        label: 'Government Aid',
      },
    },
  },

  areasOfStudy: {
    title: 'areas of study',
    fields: {
      generalAreas: {
        value: 'generalAreas',
        label: 'General',
      },
      ...areaFiltersData,
    },
  },

  ethnicity: {
    title: 'ethnicity',
    fields: {
      americanIndianAlaskaNative: {
        value: 'americanIndianAlaskaNative',
        label: 'American Indian / Alaska native',
      },
      asianPacificIslander: {
        value: 'asianPacificIslander',
        label: 'Asian / Pacific Islander',
      },
      blackNonHispanic: {
        value: 'blackNonHispanic',
        label: 'Black, non-Hispanic',
      },
      hispanic: {
        value: 'hispanic',
        label: 'Hispanic',
      },
    },
  },

  otherCategories: {
    title: 'other categories',
    fields: {
      male: {
        value: 'male',
        label: 'Male',
      },
      female: {
        value: 'female',
        label: 'Female',
      },
      married: {
        value: 'married',
        label: 'Married',
      },
      single: {
        value: 'single',
        label: 'Single',
      },
      adultLearning: {
        value: 'adultLearning',
        label: 'Adult learning',
      },
      partTimeStudents: {
        value: 'partTimeStudents',
        label: 'Part-time students',
      },
    },
  },

  disability: {
    title: 'disability',
    fields: {
      hearing: {
        value: 'hearing',
        label: 'Hearing',
      },
      visual: {
        value: 'visual',
        label: 'Visual',
      },
      physical: {
        value: 'physical',
        label: 'Physical',
      },
      learning: {
        value: 'learning',
        label: 'Learning',
      },
    },
  },
};

const areaLabelsByValue = searchFilters.areasOfStudy.filters.reduce(
  (accumulator, { label, value }) => ({
    ...accumulator,
    [`area${value}`]: FILTERS_DATA.areasOfStudy.fields![`area${value}`].label,
  }),
  {},
);

export const FILTER_LABEL_BY_VALUE: { [key: string]: string } = {
  nonEssay: FILTERS_DATA.requirements.fields!.nonEssay.label,
  essay: FILTERS_DATA.requirements.fields!.essay.label,
  militaryService: FILTERS_DATA.requirements.fields!.militaryService.label,
  noApplicationFee: FILTERS_DATA.requirements.fields!.noApplicationFee.label,

  scholarship: FILTERS_DATA.type.fields!.scholarship.label,
  fellowship: FILTERS_DATA.type.fields!.fellowship.label,
  grant: FILTERS_DATA.type.fields!.grant.label,
  forgivableLoan: FILTERS_DATA.type.fields!.forgivableLoan.label,
  prize: FILTERS_DATA.type.fields!.prize.label,

  moneyRange: 'Awards worth up to',

  freshman: FILTERS_DATA.levelOfStudy.fields!.freshman.label,
  sophomore: FILTERS_DATA.levelOfStudy.fields!.sophomore.label,
  junior: FILTERS_DATA.levelOfStudy.fields!.junior.label,
  senior: FILTERS_DATA.levelOfStudy.fields!.senior.label,
  firstProfessionalDegree: FILTERS_DATA.levelOfStudy.fields!.firstProfessionalDegree.label,
  postgraduate: FILTERS_DATA.levelOfStudy.fields!.postgraduate.label,

  university: FILTERS_DATA.institutionType.fields!.university.label,
  twoYearCollege: FILTERS_DATA.institutionType.fields!.twoYearCollege.label,
  fourYearCollege: FILTERS_DATA.institutionType.fields!.fourYearCollege.label,
  technicalSchool: FILTERS_DATA.institutionType.fields!.technicalSchool.label,

  institutionAid: FILTERS_DATA.awardClassification.fields!.institutionAid.label,
  privateAid: FILTERS_DATA.awardClassification.fields!.privateAid.label,
  governmentAid: FILTERS_DATA.awardClassification.fields!.governmentAid.label,

  generalAreas: FILTERS_DATA.areasOfStudy.fields!.generalAreas.label,
  ...areaLabelsByValue,

  americanIndianAlaskaNative: FILTERS_DATA.ethnicity.fields!.americanIndianAlaskaNative.label,
  asianPacificIslander: FILTERS_DATA.ethnicity.fields!.asianPacificIslander.label,
  blackNonHispanic: FILTERS_DATA.ethnicity.fields!.blackNonHispanic.label,
  hispanic: FILTERS_DATA.ethnicity.fields!.hispanic.label,

  male: FILTERS_DATA.otherCategories.fields!.male.label,
  female: FILTERS_DATA.otherCategories.fields!.female.label,
  married: FILTERS_DATA.otherCategories.fields!.married.label,
  single: FILTERS_DATA.otherCategories.fields!.single.label,
  adultLearning: FILTERS_DATA.otherCategories.fields!.adultLearning.label,
  partTimeStudents: FILTERS_DATA.otherCategories.fields!.partTimeStudents.label,

  hearing: FILTERS_DATA.disability.fields!.hearing.label,
  visual: FILTERS_DATA.disability.fields!.visual.label,
  physical: FILTERS_DATA.disability.fields!.physical.label,
  learning: FILTERS_DATA.disability.fields!.learning.label,
};

export const FILTERS_TYPES: { [key: string]: string } = {
  string: 'string',
  falseSearchFields: 'falseSearchFields',
  trueSearchFields: 'trueSearchFields',
  boolean: 'boolean',
  number: 'number',
  stateResidence: 'stateResidence',
  areasOfStudy: 'areasOfStudy',
  moneyRange: 'moneyRange',
};

const areasQueryData = searchFilters.areasOfStudy.filters.reduce(
  (accumulator, { label, value }) => ({
    ...accumulator,
    [`area${value}`]: {
      type: FILTERS_TYPES.areasOfStudy,
      value,
    },
  }),
  {},
);

export const FILTERS_QUERY_DATA: { [key: string]: { type: string; value?: string } } = {
  keywords: {
    type: FILTERS_TYPES.string,
  },

  nonEssay: {
    type: FILTERS_TYPES.falseSearchFields,
    value: 'applicationRequirements.essay',
  },
  essay: {
    type: FILTERS_TYPES.trueSearchFields,
    value: 'applicationRequirements.essay',
  },
  militaryService: {
    type: FILTERS_TYPES.boolean,
  },
  noApplicationFee: {
    type: FILTERS_TYPES.falseSearchFields,
    value: 'applicationFee.feeRequired',
  },

  scholarship: {
    type: FILTERS_TYPES.trueSearchFields,
    value: 'awardType.isScholarship',
  },
  fellowship: {
    type: FILTERS_TYPES.trueSearchFields,
    value: 'awardType.isFellowship',
  },
  grant: {
    type: FILTERS_TYPES.trueSearchFields,
    value: 'awardType.isGrant',
  },
  forgivableLoan: {
    type: FILTERS_TYPES.trueSearchFields,
    value: 'awardType.isForgivableLoan',
  },
  prize: {
    type: FILTERS_TYPES.trueSearchFields,
    value: 'awardType.isPrize',
  },

  moneyRange: {
    type: FILTERS_TYPES.moneyRange,
  },

  freshman: {
    type: FILTERS_TYPES.trueSearchFields,
    value: 'levelOfStudy.freshman',
  },
  sophomore: {
    type: FILTERS_TYPES.trueSearchFields,
    value: 'levelOfStudy.sophomore',
  },
  junior: {
    type: FILTERS_TYPES.trueSearchFields,
    value: 'levelOfStudy.junior',
  },
  senior: {
    type: FILTERS_TYPES.trueSearchFields,
    value: 'levelOfStudy.senior',
  },
  firstProfessionalDegree: {
    type: FILTERS_TYPES.trueSearchFields,
    value: 'levelOfStudy.graduate',
  },
  postgraduate: {
    type: FILTERS_TYPES.trueSearchFields,
    value: 'levelOfStudy.postGraduate',
  },

  university: {
    type: FILTERS_TYPES.trueSearchFields,
    value: 'institutionType.university',
  },
  twoYearCollege: {
    type: FILTERS_TYPES.trueSearchFields,
    value: 'institutionType.twoYearCollege',
  },
  fourYearCollege: {
    type: FILTERS_TYPES.trueSearchFields,
    value: 'institutionType.fourYearCollege',
  },
  technicalSchool: {
    type: FILTERS_TYPES.trueSearchFields,
    value: 'institutionType.tradeSchool',
  },

  stateResidence: {
    type: FILTERS_TYPES.stateResidence,
  },

  institutionAid: {
    type: FILTERS_TYPES.trueSearchFields,
    value: 'classification.isInstitutionalAid',
  },
  privateAid: {
    type: FILTERS_TYPES.trueSearchFields,
    value: 'classification.isPrivateAid',
  },
  governmentAid: {
    type: FILTERS_TYPES.trueSearchFields,
    value: 'classification.isGovernmentAid',
  },

  generalAreas: {
    type: FILTERS_TYPES.areasOfStudy,
    value: '0',
  },
  ...areasQueryData,

  americanIndianAlaskaNative: {
    type: FILTERS_TYPES.trueSearchFields,
    value: 'race.americanIndianAlaskaNative',
  },
  asianPacificIslander: {
    type: FILTERS_TYPES.trueSearchFields,
    value: 'race.asianPacificIslander',
  },
  blackNonHispanic: {
    type: FILTERS_TYPES.trueSearchFields,
    value: 'race.blackNonHispanic',
  },
  hispanic: {
    type: FILTERS_TYPES.trueSearchFields,
    value: 'race.hispanic',
  },

  male: {
    type: FILTERS_TYPES.trueSearchFields,
    value: 'gender.menOnly',
  },
  female: {
    type: FILTERS_TYPES.trueSearchFields,
    value: 'gender.womenOnly',
  },
  married: {
    type: FILTERS_TYPES.trueSearchFields,
    value: 'maritalStatus.married',
  },
  single: {
    type: FILTERS_TYPES.trueSearchFields,
    value: 'maritalStatus.single',
  },
  adultLearning: {
    type: FILTERS_TYPES.trueSearchFields,
    value: 'programCategories.adult',
  },
  partTimeStudents: {
    type: FILTERS_TYPES.trueSearchFields,
    value: 'workStatus.partTimeOnly,workStatus.fullOrPartTime',
  },

  hearing: {
    type: FILTERS_TYPES.trueSearchFields,
    value: 'disabilityType.hearing',
  },
  visual: {
    type: FILTERS_TYPES.trueSearchFields,
    value: 'disabilityType.visual',
  },
  physical: {
    type: FILTERS_TYPES.trueSearchFields,
    value: 'disabilityType.physical',
  },
  learning: {
    type: FILTERS_TYPES.trueSearchFields,
    value: 'disabilityType.learning',
  },
};
